import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { alpha, darken } from '@mui/material/styles'
import { FlagsIconSeeker, IconSeeker } from '@ui-seeker/core'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { AppCardTitle, AppDialog, AppDialogBody, AppDialogHeader, AppEmptyLine, AppFilterChip, AppFilterPanel, AppResultsSkeleton, AppNote, AppTableSkeleton } from '@components'
import { displayDay } from '@utils'
import { dsColorNew, dataColors } from '@palette'
import { MbPage, MbKpiRow, mbScopeSx, MbPageSkeleton } from 'Pages/MyBusiness/_shared/Ui'

import {
	ApplyButton,
	DatePresets,
	DateRangeField,
	FORWARD_PRESETS,
	mbFieldsGridSx,
	OfficeSlicer,
	CategorySlicer,
	ProviderSlicer,
	TierSlicer,
	MarketSlicer,
	InsuranceSlicer,
	flagOf,
} from 'Pages/MyBusiness/_shared/Filters'
import AcrissIcon from 'Pages/MyBusiness/_shared/AcrissIcon'
import { DownloadCsvButton } from 'Pages/MyBusiness/_shared/Charts'

/**
 * B2 — Price Positioning, office-first.
 *
 * For each PMS office, a single index summarises where the operator's
 * published prices sit relative to the rest of the broker results:
 *
 *   index = mi_median / market_median * 100
 *     >105 → expensive vs market
 *     95-105 → aligned
 *     <95 → cheap vs market
 *
 * Office index is a weighted avg of per-category indexes, weighted by
 * MY OWN quote count in each category — so the categories I sell more
 * of dominate the office number. Offices sort worst-first (highest
 * index) by default — that's where pricing review should start.
 *
 * Drill-down: each office accordion shows its categories worst-to-best
 * with their own index. A future click could go into the B1' provider
 * ranking for that (office × category) slice.
 */
const fmtPct = (n, opts = {}) =>
	n == null ? '—'
	: `${opts.sign && n > 0 ? '+' : ''}${(Math.round(n * 10) / 10).toFixed(1)}`
const fmtEur = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR',
		minimumFractionDigits: 2, maximumFractionDigits: 2,
	}).format(n)
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)

const LOR_OPTIONS = ['', 1, 2, 3, 5, 7, 14, 21, 28]

function iso(d) { return d.toISOString().slice(0, 10) }
function defaultRange() {
	const today = new Date()
	const t30   = new Date(today); t30.setDate(t30.getDate() + 30)
	return { preset: 'next30', from: iso(today), to: iso(t30) }
}

// Pace overlay v1 — booking pace status visualisation.
const PACE_ICON  = { ahead: 'caret-up', on_pace: 'circle', slow: 'caret-down' }
const PACE_COLOR = { ahead: dataColors.green600, on_pace: dsColorNew.textSubtle, slow: dsColorNew.dangerRed }
const PACE_LABEL = {
	ahead:   'Ahead of pace',
	on_pace: 'On pace',
	slow:    'Slow pace',
}
// The pace mark as an icon; the dot is drawn smaller so it weighs as much as the carets
const PaceGlyph = ({ status, size }) => <IconSeeker icon={PACE_ICON[status]} sx={{ fontSize: status === 'on_pace' ? Math.round(size * 0.7) : size, color: 'inherit' }}/>

// Colour bands for the price index. Higher = more expensive. Soft tints from green (cheaper than the
// market) through grey (aligned) to red, the figure in the darker tone of its band; only the extremes are strong.
function indexBand(idx) {
	if (idx == null) return { color: dsColorNew.textSubtle, label: '—',  bg: dsColorNew.filterInputBg }
	if (idx > 140)   return { color: dsColorNew.white, label: 'Extreme high', bg: dsColorNew.dangerSoft }
	if (idx > 125)   return { color: dataColors.red900, label: 'Very high',    bg: dataColors.red300 }
	if (idx > 115)   return { color: dataColors.red800, label: 'High',         bg: dsColorNew.dangerBorder }
	if (idx > 108)   return { color: dataColors.red700, label: 'Above',        bg: dataColors.red100 }
	if (idx > 102)   return { color: dataColors.red700, label: 'Slightly high',bg: dsColorNew.dangerBg }
	if (idx >= 98)   return { color: dsColorNew.textDark, label: 'Aligned',      bg: dsColorNew.pillTrackBg }
	if (idx >= 92)   return { color: dsColorNew.successText, label: 'Slightly low', bg: dsColorNew.successBg }
	if (idx >= 85)   return { color: dsColorNew.successText, label: 'Below',        bg: dataColors.green100 }
	if (idx >= 75)   return { color: dataColors.green800, label: 'Very low',     bg: dataColors.mint200 }
	return                  { color: dataColors.green900, label: 'Extreme low',  bg: dataColors.mint300 }
}

// The index as a soft pill in its band colour
function IndexPill({ index, size = 'medium', sx = {} }) {
	const band = indexBand(index)
	const big = size === 'large'
	return <Box component="span" sx={{
		display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
		minWidth: big ? 0 : 52, height: big ? 36 : 24, px: big ? 1.25 : 1, borderRadius: big ? '10px' : '12px',
		bgcolor: band.bg, color: band.color, fontSize: big ? 22 : 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', ...sx,
	}}>
		{fmtPct(index)}
	</Box>
}

export default function MyBusinessMarketPage() {
	const [draft,   setDraft]   = useState({
		date: defaultRange(), offices: [], categories: [],
		providers: [], tiers: [], markets: [], lor: '',
		insurance: 'all',
	})
	const [applied, setApplied] = useState(draft)
	const [data,    setData]    = useState(null)
	const [summary, setSummary] = useState(null)
	const [error,   setError]   = useState(null)
	const [loading, setLoading] = useState(true)
	const pageLoading = loading
	const [openCell, setOpenCell] = useState(null)

	// Drilling into a cell from the heatmap or a banner alert tile goes
	// through the same dialog. The page owns the state so the banner can
	// open it without sharing context with the heatmap row.
	const openCellFromLeaf = useCallback((args) => setOpenCell(args), [])
	const openCellFromSummary = useCallback((cell) => {
		if (!cell || !data) return
		const fullOffice = (data.offices || []).find(o => o.office_code === cell.office_code)
		                   || { office_code: cell.office_code, office_name: '', country_code: null }
		setOpenCell({
			office: fullOffice,
			cat:    { sipp_code: cell.sipp_code, ps_acriss: null },
			pickup: cell.pickup_date,
			lor:    cell.lor,
			leaf:   {
				index:         cell.price_index,
				mi:            cell.mi,
				mkt:           cell.mkt,
				n_mi:          cell.n_mi,
				n_mkt:         cell.n_mkt,
				pace_status:   cell.pace_status,
				pace_actual:   cell.pace_actual,
				pace_expected: cell.pace_expected,
				dba:           cell.dba,
			},
		})
	}, [data])

	const buildQs = (f) => {
		const qs = new URLSearchParams({ from: f.date.from, to: f.date.to })
		if (f.offices.length)    qs.set('offices', f.offices.join(','))
		if (f.categories.length) qs.set('sipps',   f.categories.join(','))
		const providerIds = new Set(f.providers || [])
		;(f.tierProviders || []).forEach(p => providerIds.add(p))
		if (providerIds.size) qs.set('providers', [...providerIds].join(','))
		if (f.markets && f.markets.length) qs.set('markets', f.markets.join(','))
		if (f.lor) qs.set('lor', String(f.lor))
		if (f.insurance && f.insurance !== 'all') qs.set('insurance', f.insurance)
		return qs
	}

	const fetchData = useCallback((f) => {
		setLoading(true); setError(null)
		const qs = buildQs(f)
		// Summary fires in parallel — its result is independent and only
		// drives the KPI banner; failures don't block the heatmap.
		fetch(`/api/my-business/market/positioning/summary?${qs}`,
		      { credentials: 'include' })
			.then(r => r.ok ? r.json() : null)
			.then(setSummary)
			.catch(() => setSummary(null))
		fetch(`/api/my-business/market/positioning?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [])

	const applyInstant = (patch) => {
		const next = { ...applied, ...patch }
		setApplied(next); setDraft(next); fetchData(next)
	}
	const applyManual = () => {
		setApplied(draft); fetchData(draft)
	}
	const applyDate = (date, opts) => {
		if (opts?.intent === 'instant') applyInstant({ date })
		else setDraft({ ...draft, date })
	}
	const hasPending = (
		draft.date.from !== applied.date.from ||
		draft.date.to   !== applied.date.to
	)

	useEffect(() => { fetchData(applied) }, [])

	return (
		<MbPage title={MY_BUSINESS_ROUTES.MARKET_TITLE}
		        subtitle="Where my published prices sit vs the rest of the market">
			<Box>
				<AppFilterPanel
					toggles={<DatePresets value={draft.date}
					                      onChange={applyDate}
					                      presets={FORWARD_PRESETS}/>}
					actions={<>
						<DownloadCsvButton
							href={`/api/my-business/market/positioning/download?${buildQs(applied)}`}
							from={applied.date.from}
							to={applied.date.to}
							label="Market CSV"/>
						<ApplyButton onClick={applyManual}
						             disabled={!hasPending || loading}>
							{loading ? 'Loading…' : hasPending ? 'Apply' : 'Up to date'}
						</ApplyButton>
					</>}
				>
					<Box sx={mbFieldsGridSx()}>
						<DateRangeField value={draft.date}
						                onChange={applyDate}/>
						<MarketSlicer value={applied.markets}
						              onChange={(markets) => applyInstant({ markets })}/>
						<OfficeSlicer value={applied.offices}
						              onChange={(offices) => applyInstant({ offices })}/>
						<CategorySlicer value={applied.categories}
						                onChange={(categories) => applyInstant({ categories })}/>
						<ProviderSlicer value={applied.providers}
						                onChange={(providers) => applyInstant({ providers })}/>
						<TierSlicer value={applied.tiers}
						            onChange={(tiers, tierProviders) =>
						              applyInstant({ tiers, tierProviders })}/>
						<TextField select size="small" label="LOR"
						           value={applied.lor}
						           onChange={(e) => applyInstant({ lor: e.target.value })}>
							{LOR_OPTIONS.map(l => (
								<MenuItem key={l} value={l}>{l === '' ? 'Any' : `${l} d`}</MenuItem>
							))}
						</TextField>
						<InsuranceSlicer value={applied.insurance || 'all'}
						                 onChange={(insurance) => applyInstant({ insurance })}/>
					</Box>
				</AppFilterPanel>

				{error && <AppNote severity="error" sx={{ mb: 2 }}>Market data unreachable: {error}</AppNote>}

				{pageLoading && <MbPageSkeleton kpis={4} blocks={['list']}/>}

				{data && !pageLoading && data.blocked_by && <BlockedAlert reason={data.blocked_by}/>}

				{data && !pageLoading && !data.blocked_by && (
					<Box>
						<KpiBanner kpi={data.kpi} summary={summary}
						           onOpenCell={openCellFromSummary}/>

						{data.offices.length === 0 ? (
							<Paper sx={{ p: 3, mt: 3 }}>
								<AppEmptyLine>
									No comparable price data in this window. Widen the date range
									or check Price Seeker coverage for these offices.
								</AppEmptyLine>
							</Paper>
						) : (
							<OfficesTable offices={data.offices}
							              filters={applied}
							              onOpenCell={openCellFromLeaf}/>
						)}
					</Box>
				)}
			</Box>
			{openCell && (
				<CellDetailDialog open
				                  onClose={() => setOpenCell(null)}
				                  office={openCell.office}
				                  cat={openCell.cat}
				                  pickup={openCell.pickup}
				                  lor={openCell.lor}
				                  leaf={openCell.leaf}
				                  filters={applied}/>
			)}
		</MbPage>
	)
}

// ---------------------------------------------------------------------------
// KPI banner — 4 tiles:
//  · Avg price index (overall position vs market)
//  · Cheapest competitor (where the floor sits)
//  · Biggest gap (priced high + selling slow — the alarm)
//  · Sub-pricing alert (priced low + selling hot — leaving money)
// ---------------------------------------------------------------------------
function KpiBanner({ kpi, summary, onOpenCell }) {
	const band = indexBand(kpi.avg_index)
	return (
		<MbKpiRow min={220}>
			<KpiAvgIndex kpi={kpi} band={band}/>
			<KpiCheapest provider={summary?.cheapest_provider}/>
			<KpiGapAlert cell={summary?.biggest_gap}
			             title="Biggest gap"
			             severity="danger"
			             helper="expensive and selling slow"
			             emptyText="No overpricing alerts"
			             onOpenCell={onOpenCell}/>
			<KpiGapAlert cell={summary?.subpricing}
			             title="Sub-pricing"
			             severity="warn"
			             helper="cheap and selling fast"
			             emptyText="No sub-pricing alerts"
			             onOpenCell={onOpenCell}/>
		</MbKpiRow>
	)
}

const KPI_TONES = {
	danger: { accent: dsColorNew.dangerRed, bg: dsColorNew.dangerBg,    border: dsColorNew.dangerBorder,    icon: 'arrow-trend-up' },
	warn:   { accent: dsColorNew.noteAmber, bg: dsColorNew.noteAmberBg, border: dsColorNew.noteAmberBorder, icon: 'arrow-trend-down' },
}

// A banner card as the other figure cards of the app: white, soft border, icon tile and the label in small capitals.
// `tone` gives an alert its soft tint; `onClick` makes it lift on hover.
function KpiShell({ icon, color = dsColorNew.webBlue500, label, labelColor = dsColorNew.textMuted, extra, tone, onClick, children }) {
	return (
		<Box onClick={onClick}
		     sx={{
			     height: 1, display: 'flex', flexDirection: 'column',
			     p: '16px 18px', borderRadius: '16px',
			     backgroundColor: dsColorNew.white,
			     border: `1px solid ${dsColorNew.grayBorder}`,
			     boxShadow: `0 1px 2px ${alpha(dsColorNew.navy, 0.04)}`,
			     cursor: onClick ? 'pointer' : 'default',
			     transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
			     '&:hover': onClick ? {
				     transform: 'translateY(-2px)',
				     borderColor: color,
				     boxShadow: `0 6px 16px ${alpha(dsColorNew.navy, 0.08)}`,
			     } : {},
		     }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
				<Box sx={{
					width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					backgroundColor: alpha(color, 0.12),
				}}>
					<IconSeeker icon={icon} sx={{ color, fontSize: 14 }}/>
				</Box>
				<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
				                   color: labelColor, lineHeight: 1.35 }}>
					{label}
				</Typography>
				{extra}
			</Box>
			{children}
		</Box>
	)
}

function KpiAvgIndex({ kpi, band }) {
	return (
		<KpiShell icon="scale-balanced" label="Avg price index">
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
				<IndexPill index={kpi.avg_index} size="large"/>
				<Typography sx={{ fontSize: 12.5, fontWeight: 600, color: band.color }}>
					{band.label}
				</Typography>
			</Box>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, mt: 'auto', pt: 1 }}>
				{kpi.n_offices} office{kpi.n_offices !== 1 ? 's' : ''} ·{' '}
				{fmtNum(kpi.n_quotes)} quote{kpi.n_quotes !== 1 ? 's' : ''}
			</Typography>
		</KpiShell>
	)
}

function KpiCheapest({ provider }) {
	return (
		<KpiShell icon="tag" label="Cheapest competitor">
			{provider ? (
				<>
					<Typography sx={{ fontSize: 16, fontWeight: 700, mt: 1.25, color: dsColorNew.textTitle,
					                   lineHeight: 1.2,
					                   whiteSpace: 'nowrap', overflow: 'hidden',
					                   textOverflow: 'ellipsis' }}>
						{provider.provider_name}
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
						<Typography sx={{ fontSize: 22, fontWeight: 700, color: dsColorNew.textTitle,
						                   fontVariantNumeric: 'tabular-nums' }}>
							{fmtEur(provider.median_price)}
						</Typography>
						<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
							median
						</Typography>
					</Box>
					<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, mt: 'auto', pt: 1 }}>
						{fmtNum(provider.n_quotes)} quotes in slice
					</Typography>
				</>
			) : (
				<KpiEmpty text="No competitor data"/>
			)}
		</KpiShell>
	)
}

function KpiGapAlert({ cell, title, severity, helper, emptyText, onOpenCell }) {
	const tone   = KPI_TONES[severity === 'danger' ? 'danger' : 'warn']
	const accent = tone.accent
	const clickable = Boolean(cell && onOpenCell)
	if (!cell) {
		return (
			<KpiShell icon={tone.icon} color={accent} label={title}>
				<KpiEmpty text={emptyText}/>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, mt: 'auto', pt: 1,
				                   fontStyle: 'italic' }}>
					{helper}
				</Typography>
			</KpiShell>
		)
	}
	const dt = new Date(`${cell.pickup_date}T00:00:00`)
	const pickupShort = `${dt.getDate()} ${dt.toLocaleDateString('en-GB', { month: 'short' })}`
	const paceStatus = cell.pace_status
	return (
		<KpiShell icon={tone.icon} color={accent} label={title}
		          onClick={clickable ? () => onOpenCell(cell) : undefined}
		          extra={paceStatus && (
			          <Box sx={{
				          width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
				          bgcolor: PACE_COLOR[paceStatus], color: dsColorNew.white,
				          lineHeight: 1,
				          display: 'flex', alignItems: 'center', justifyContent: 'center',
				          boxShadow: `0 0 0 1.5px ${alpha(dsColorNew.white, 0.95)}`,
			          }}>
				          <PaceGlyph status={paceStatus} size={10}/>
			          </Box>
		          )}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
				<IndexPill index={cell.price_index} size="large"/>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
					index
				</Typography>
			</Box>
			<Typography sx={{ fontSize: 14, fontWeight: 600, mt: 0.75, color: dsColorNew.textTitle,
			                   lineHeight: 1.25 }}>
				{cell.office_code} · {cell.sipp_code}
			</Typography>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				{pickupShort} · {cell.lor}d rental
				{cell.pace_actual != null && (
					<> · util {Math.round(cell.pace_actual)}% vs {Math.round(cell.pace_expected ?? 0)}%</>
				)}
			</Typography>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, mt: 'auto', pt: 1, fontStyle: 'italic' }}>
				{helper}
			</Typography>
		</KpiShell>
	)
}

function KpiEmpty({ text }) {
	return (
		<Typography sx={{ fontSize: 16, fontWeight: 600, mt: 1.25,
		                   color: dsColorNew.textSubtle }}>
			{text}
		</Typography>
	)
}

// ---------------------------------------------------------------------------
// Offices — one table; an office opens its categories, a category its heatmap
// ---------------------------------------------------------------------------
// A table inside an open row: a light header instead of the navy one
const nestedTableSx = {
	backgroundColor: dsColorNew.white,
	'& .MuiTableHead-root .MuiTableCell-root': {
		backgroundColor: `${dsColorNew.pillTrackBg} !important`, color: `${dsColorNew.textMuted} !important`, fontSize: 11.5, py: 0.75,
	},
	'& > .MuiTable-root > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root': { backgroundColor: `${dsColorNew.white} !important` },
}
const openRowSx = { '& > .MuiTableCell-root': { backgroundColor: `${dsColorNew.pillActiveBg} !important` } }
const chevron = (isOpen) => <IconSeeker icon={isOpen ? 'chevron-down' : 'chevron-right'} sx={{ fontSize: 11, color: dsColorNew.textMuted }}/>

function OfficesTable({ offices, filters, onOpenCell }) {
	// Offices whose categories are open; all start closed
	const [open, setOpen] = useState(() => new Set())
	const toggle = (code) => setOpen(prev => {
		const next = new Set(prev)
		if (next.has(code)) next.delete(code)
		else                next.add(code)
		return next
	})
	return (
		<Paper sx={{ p: 2.5, mt: 3 }}>
			<AppCardTitle icon="location-dot" title="Offices" sx={{ mb: 1.5 }}/>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell width={36}/>
							<TableCell>Office</TableCell>
							<TableCell>Index</TableCell>
							<TableCell/>
							<TableCell align="right">Categories</TableCell>
							<TableCell align="right">Own quotes</TableCell>
							<TableCell align="right">Market quotes</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{offices.map(o => (
							<OfficeRows key={o.office_code} office={o} filters={filters} onOpenCell={onOpenCell}
							            isOpen={open.has(o.office_code)} onToggle={() => toggle(o.office_code)}/>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	)
}

function OfficeRows({ office, filters, onOpenCell, isOpen, onToggle }) {
	const band = indexBand(office.index)
	return (
		<>
			<TableRow hover onClick={onToggle} sx={{ cursor: 'pointer', ...(isOpen && openRowSx) }}>
				<TableCell sx={{ pr: 0 }}>{chevron(isOpen)}</TableCell>
				<TableCell>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
						{office.country_code && <FlagsIconSeeker countryName={office.country_code}/>}
						<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{office.office_code}</Box>
						{office.office_name && <Box component="span" sx={{ color: dsColorNew.textMuted }}>{office.office_name}</Box>}
					</Box>
				</TableCell>
				<TableCell><IndexPill index={office.index}/></TableCell>
				<TableCell sx={{ fontSize: 12.5, fontWeight: 600, color: `${band.color === dsColorNew.white ? dataColors.red800 : band.color} !important`, whiteSpace: 'nowrap' }}>
					{band.label}
				</TableCell>
				<TableCell align="right">{fmtNum(office.n_categories)}</TableCell>
				<TableCell align="right">{fmtNum(office.n_mi_quotes)}</TableCell>
				<TableCell align="right">{fmtNum(office.n_mkt_quotes)}</TableCell>
			</TableRow>
			{isOpen && (
				<TableRow>
					<TableCell/>
					<TableCell colSpan={6} sx={{ pt: 1, pb: 2, backgroundColor: `${dsColorNew.tableRowAlt} !important` }}>
						{/* width 0 + min-width 100%: the wide heatmap inside scrolls instead of widening the offices table */}
						<Box sx={{ width: 0, minWidth: '100%' }}>
						<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mb: 1 }}>
							Categories sorted worst-positioned first. Click a row to drill into LOR / DOR / leaf detail.
						</Typography>
						<TableContainer sx={nestedTableSx}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell width={32}/>
										<TableCell>SIPP</TableCell>
										<TableCell>Index</TableCell>
										<TableCell align="right">Own avg</TableCell>
										<TableCell align="right">Market avg</TableCell>
										<TableCell align="right">Own · market quotes</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{office.categories.map((c) => (
										<CategoryRows key={c.sipp_code}
										              office={office} cat={c}
										              filters={filters}
										              onOpenCell={onOpenCell}/>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						</Box>
					</TableCell>
				</TableRow>
			)}
		</>
	)
}

function CategoryRows({ office, cat, filters, onOpenCell }) {
	const [detail, setDetail] = useState(null)
	const [error, setError]   = useState(null)
	const [loading, setLoading] = useState(false)
	const [opened, setOpened] = useState(false)

	// Signature of the filter inputs that drive /detail. When this string
	// changes we drop the cached detail so a stale heatmap never lingers
	// after the user touches the filter bar.
	const filterKey = useMemo(() => JSON.stringify({
		from:          filters.date.from,
		to:            filters.date.to,
		lor:           filters.lor || null,
		providers:     [...(filters.providers || [])].sort(),
		tierProviders: [...(filters.tierProviders || [])].sort(),
		markets:       [...(filters.markets || [])].sort(),
	}), [filters])

	const fetchDetail = useCallback(() => {
		setLoading(true); setError(null)
		const qs = new URLSearchParams({
			office: office.office_code,
			sipp:   cat.sipp_code,
			from:   filters.date.from,
			to:     filters.date.to,
		})
		if (filters.lor) qs.set('lor', String(filters.lor))
		const providerIds = new Set(filters.providers || [])
		;(filters.tierProviders || []).forEach(p => providerIds.add(p))
		if (providerIds.size) qs.set('providers', [...providerIds].join(','))
		if (filters.markets && filters.markets.length)
			qs.set('markets', filters.markets.join(','))
		fetch(`/api/my-business/market/positioning/detail?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setDetail)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	// fetchDetail intentionally only depends on what changes the URL.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [office.office_code, cat.sipp_code, filterKey])

	// Invalidate cache on any filter change. If currently expanded, refetch
	// in place so the heatmap stays in sync with the filter bar.
	useEffect(() => {
		setDetail(null)
		if (opened) fetchDetail()
	// Only the filter signature should re-trigger; `opened` is read above.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterKey])

	const toggle = () => {
		const next = !opened
		setOpened(next)
		if (next && !detail && !loading) fetchDetail()
	}

	return (
		<>
			<TableRow hover onClick={toggle} sx={{ cursor: 'pointer', ...(opened && openRowSx) }}>
				<TableCell sx={{ pr: 0 }}>{chevron(opened)}</TableCell>
				<TableCell>
					<Box display="flex" alignItems="center" gap={1}>
						<AcrissIcon sipp={cat.sipp_code} size={24}/>
						<Box>
							<Box sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{cat.sipp_code}</Box>
							{cat.ps_acriss !== cat.sipp_code && (
								<Box sx={{ fontSize: 11.5, color: dsColorNew.textMuted }}>↪ {cat.ps_acriss}</Box>
							)}
						</Box>
					</Box>
				</TableCell>
				<TableCell><IndexPill index={cat.index}/></TableCell>
				<TableCell align="right">
					<Tooltip arrow placement="top"
					         title={`Based on ${cat.leaves} pickup/LOR slot${cat.leaves !== 1 ? 's' : ''}`}>
						<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textTitle, cursor: 'help' }}>{fmtEur(cat.mi_avg)}</Box>
					</Tooltip>
				</TableCell>
				<TableCell align="right" sx={{ color: `${dsColorNew.textMuted} !important` }}>{fmtEur(cat.mkt_avg)}</TableCell>
				<TableCell align="right" sx={{ color: `${dsColorNew.textMuted} !important`, whiteSpace: 'nowrap' }}>
					{fmtNum(cat.n_mi_quotes)} · {fmtNum(cat.n_mkt_quotes)}
				</TableCell>
			</TableRow>
			{opened && (
				<TableRow>
					<TableCell colSpan={6} sx={{ pt: 0.5, pb: 2 }}>
						<Box sx={{ width: 0, minWidth: '100%' }}>
						{loading && <AppTableSkeleton rows={4} columns={8} sx={{ mt: 1 }}/>}
						{error && <AppNote severity="error" sx={{ my: 1 }}>{error}</AppNote>}
						{detail && !loading && (
							<DetailPanel detail={detail} office={office} cat={cat} filters={filters} onOpenCell={onOpenCell}/>
						)}
						</Box>
					</TableCell>
				</TableRow>
			)}
		</>
	)
}

function DetailPanel({ detail, office, cat, filters, onOpenCell }) {
	if (!detail.detail.length) {
		return (
			<AppEmptyLine>Not enough comparable rows for this slice.</AppEmptyLine>
		)
	}
	return (
		<Box>
			<LeafTable rows={detail.detail} office={office} cat={cat} filters={filters} onOpenCell={onOpenCell}/>
		</Box>
	)
}

function CellTooltip({ r, d, lor, band }) {
	const dt = new Date(`${d}T00:00:00`)
	const wd = dt.toLocaleDateString('en-GB', { weekday: 'short' })
	const mo = dt.toLocaleDateString('en-GB', { month: 'short' })
	const headline = `${wd} ${dt.getDate()} ${mo} ${dt.getFullYear()} · ${lor}-day rental`
	const deltaPct = (r.mi && r.mkt) ? ((r.mi - r.mkt) / r.mkt) * 100 : null
	const deltaEur = (r.mi != null && r.mkt != null) ? (r.mi - r.mkt) : null

	const MUTED   = alpha(dsColorNew.white, 0.65)
	const FAINT   = alpha(dsColorNew.white, 0.15)
	const RED_FG  = dataColors.red300
	const GRN_FG  = dataColors.green300

	return (
		<Box sx={{ p: 1.75, minWidth: 320 }}>
			<Typography sx={{ fontSize: 14, fontWeight: 800,
			                   color: dsColorNew.white, mb: 1.25 }}>
				{headline}
			</Typography>

			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
				<Box sx={{
					bgcolor: band.bg, color: band.color,
					borderRadius: 1, px: 1.25, py: 0.75,
					fontSize: 22, fontWeight: 800, lineHeight: 1,
					fontVariantNumeric: 'tabular-nums', minWidth: 60, textAlign: 'center',
				}}>
					{r.index != null ? r.index.toFixed(1) : '—'}
				</Box>
				<Typography sx={{ fontSize: 12, color: alpha(dsColorNew.white, 0.9),
				                   textTransform: 'uppercase', letterSpacing: '0.06em',
				                   fontWeight: 700 }}>
					{band.label}
				</Typography>
			</Box>

			<Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto',
			            columnGap: 1.5, rowGap: 0.5, alignItems: 'baseline' }}>
				<Typography sx={{ fontSize: 11, color: MUTED,
				                   textTransform: 'uppercase', letterSpacing: '0.06em',
				                   fontWeight: 600 }}>You</Typography>
				<Typography sx={{ fontSize: 15, fontWeight: 700, color: dsColorNew.white,
				                   fontVariantNumeric: 'tabular-nums' }}>
					{r.mi != null ? fmtEur(r.mi) : '—'}
				</Typography>
				<Box/>

				<Typography sx={{ fontSize: 11, color: MUTED,
				                   textTransform: 'uppercase', letterSpacing: '0.06em',
				                   fontWeight: 600 }}>Market</Typography>
				<Typography sx={{ fontSize: 15, fontWeight: 700, color: dsColorNew.white,
				                   fontVariantNumeric: 'tabular-nums' }}>
					{r.mkt != null ? fmtEur(r.mkt) : '—'}
				</Typography>
				{deltaPct != null && (
					<Typography sx={{ fontSize: 13, fontWeight: 700,
					                   textAlign: 'right',
					                   color: deltaPct > 0 ? RED_FG : GRN_FG,
					                   fontVariantNumeric: 'tabular-nums' }}>
						{deltaEur > 0 ? '+' : ''}{fmtEur(deltaEur)} ({deltaPct > 0 ? '+' : ''}{deltaPct.toFixed(1)}%)
					</Typography>
				)}
			</Box>

			<Box sx={{ mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: FAINT,
			            display: 'grid', gridTemplateColumns: 'auto 1fr',
			            columnGap: 1.5, rowGap: 0.5 }}>
				{r.mkt_min != null && r.mkt_max != null && r.mkt_min !== r.mkt_max && (
					<>
						<Typography sx={{ fontSize: 11, color: MUTED,
						                   textTransform: 'uppercase', letterSpacing: '0.06em',
						                   fontWeight: 600 }}>Range</Typography>
						<Typography sx={{ fontSize: 13, color: dsColorNew.white,
						                   fontVariantNumeric: 'tabular-nums' }}>
							{fmtEur(r.mkt_min)} – {fmtEur(r.mkt_max)}
						</Typography>
					</>
				)}
				<Typography sx={{ fontSize: 11, color: MUTED,
				                   textTransform: 'uppercase', letterSpacing: '0.06em',
				                   fontWeight: 600 }}>Quotes</Typography>
				<Typography sx={{ fontSize: 13, color: dsColorNew.white,
				                   fontVariantNumeric: 'tabular-nums' }}>
					{r.n_mi} own · {r.n_mkt} market
				</Typography>
			</Box>

			{r.pace_status && (
				<Box sx={{ mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: FAINT }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
						<Box sx={{
							width: 18, height: 18, borderRadius: '50%',
							bgcolor: PACE_COLOR[r.pace_status], color: dsColorNew.white,
							lineHeight: 1,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							boxShadow: `0 0 0 1.5px ${alpha(dsColorNew.white, 0.5)}`,
						}}>
							<PaceGlyph status={r.pace_status} size={12}/>
						</Box>
						<Typography sx={{ fontSize: 14, fontWeight: 700, color: dsColorNew.white }}>
							{PACE_LABEL[r.pace_status]}
						</Typography>
					</Box>
					<Typography sx={{ fontSize: 12, color: alpha(dsColorNew.white, 0.8),
					                   fontVariantNumeric: 'tabular-nums' }}>
						{r.pace_actual?.toFixed?.(0) ?? '—'}% util vs{' '}
						{r.pace_expected?.toFixed?.(0) ?? '—'}% expected
						{r.dba != null && <> · {r.dba}d to pickup</>}
					</Typography>
				</Box>
			)}

			<Typography sx={{ mt: 1.25, fontSize: 11,
			                   color: alpha(dsColorNew.white, 0.55), fontStyle: 'italic' }}>
				Click for provider-level detail.
			</Typography>
		</Box>
	)
}

function LeafTable({ rows, office, cat, filters, onOpenCell }) {
	if (!rows.length) return null

	// Pivot: rows = LOR values, cols = pickup_dates, cells = leaf.
	const lors = [...new Set(rows.map(r => r.length_of_rental))].sort((a, b) => a - b)
	const dates = [...new Set(rows.map(r => r.pickup_date).filter(Boolean))].sort()
	const cell = new Map()
	rows.forEach(r => {
		if (r.pickup_date == null) return
		cell.set(`${r.length_of_rental}|${r.pickup_date}`, r)
	})

	// Month boundaries for the date column header.
	const monthLabels = []
	let lastMonth = null
	dates.forEach((d, i) => {
		const m = d.slice(0, 7)
		if (m !== lastMonth) { monthLabels.push({ idx: i, label: m }); lastMonth = m }
	})

	return (
		<Box sx={{ mt: 1.5 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between"
			     flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
				<AppCardTitle icon="table-cells" title="Per pickup × LOR heatmap"/>
				<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>
					Colour = price index. Click any coloured cell to see every provider's price.
				</Typography>
			</Box>
			<Box sx={{ overflowX: 'auto', border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px' }}>
				<Box component="table" sx={heatmapSx}>
					<thead>
						<tr>
							<th rowSpan={2} className="fx">LOR</th>
							{monthLabels.map((m, i) => {
								const next = monthLabels[i + 1]
								return <th key={i} className="mo" colSpan={(next ? next.idx : dates.length) - m.idx}>{m.label}</th>
							})}
						</tr>
						<tr>
							{dates.map((d) => <th key={d} className="dy">{d.slice(8, 10)}</th>)}
						</tr>
					</thead>
					<tbody>
						{lors.map((lor) => (
							<tr key={lor}>
								<td className="fx">{lor}d</td>
								{dates.map((d) => {
									const r = cell.get(`${lor}|${d}`)
									const band = indexBand(r?.index)
									const tooltipNode = r
										? <CellTooltip r={r} d={d} lor={lor} band={band}/>
										: <Box sx={{ p: 1, fontSize: 13, color: dsColorNew.white }}>{displayDay(d)} · {lor}-day — no data</Box>
									return (
										<td key={d}>
											<Tooltip title={tooltipNode} arrow placement="top"
												componentsProps={{
													tooltip: { sx: {
														bgcolor: alpha(dsColorNew.navy, 0.97),
														maxWidth: 'none',
														border: `1px solid ${alpha(dsColorNew.white, 0.12)}`,
														boxShadow: 6,
														p: 0,
													}},
													arrow:   { sx: { color: alpha(dsColorNew.navy, 0.97) }},
												}}>
												<Box onClick={() => r && onOpenCell?.({ office, cat, pickup: d, lor, leaf: r })}
												     sx={{
													position: 'relative',
													height: 32, borderRadius: '6px',
													bgcolor: r ? band.bg : dsColorNew.filterInputBg,
													color: r ? band.color : dsColorNew.textSubtle,
													display: 'flex', alignItems: 'center', justifyContent: 'center',
													fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
													cursor: r ? 'pointer' : 'default',
													transition: 'transform 80ms ease',
													'&:hover': { transform: r ? 'scale(1.12)' : 'none', zIndex: 2 },
												}}>
													{r?.index != null ? Math.round(r.index) : ''}
													{/* Booking pace: a small mark in the corner */}
													{r?.pace_status && (
														<Box component="span" sx={{
															position: 'absolute', top: 1, right: 3,
															fontSize: 8, lineHeight: 1, color: PACE_COLOR[r.pace_status], pointerEvents: 'none',
														}}>
															<PaceGlyph status={r.pace_status} size={8}/>
														</Box>
													)}
												</Box>
											</Tooltip>
										</td>
									)
								})}
							</tr>
						))}
					</tbody>
				</Box>
			</Box>
			{/* Legend */}
			<Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
				<Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
					<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textMuted,
					                   textTransform: 'uppercase', minWidth: 110, letterSpacing: 1 }}>
						Price index
					</Typography>
					{[
						{ band: indexBand(70),  label: '<75' },
						{ band: indexBand(80),  label: '75–85' },
						{ band: indexBand(88),  label: '85–92' },
						{ band: indexBand(95),  label: '92–98' },
						{ band: indexBand(100), label: '98–102  aligned' },
						{ band: indexBand(105), label: '102–108' },
						{ band: indexBand(112), label: '108–115' },
						{ band: indexBand(120), label: '115–125' },
						{ band: indexBand(135), label: '125–140' },
						{ band: indexBand(150), label: '>140' },
					].map((s, i) => (
						<Box key={i} display="flex" alignItems="center" gap={0.6}>
							<Box sx={{ width: 14, height: 14, bgcolor: s.band.bg, borderRadius: '4px', border: `1px solid ${alpha(s.band.color, 0.15)}` }}/>
							<Typography sx={{ fontSize: 12, color: dsColorNew.textDark }}>{s.label}</Typography>
						</Box>
					))}
				</Box>
				<Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
					<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textMuted,
					                   textTransform: 'uppercase', minWidth: 110, letterSpacing: 1 }}>
						Booking pace
					</Typography>
					{['ahead', 'on_pace', 'slow'].map(k => (
						<Box key={k} display="flex" alignItems="center" gap={0.6}>
							<Box component="span" sx={{ display: 'inline-flex', color: PACE_COLOR[k] }}><PaceGlyph status={k} size={10}/></Box>
							<Typography sx={{ fontSize: 12, color: dsColorNew.textDark }}>{PACE_LABEL[k]}</Typography>
						</Box>
					))}
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, ml: 'auto',
					                   fontStyle: 'italic' }}>
						Pace = own utilization vs expected curve at this DBA.
						Missing glyph = no fleet data or signal too weak.
					</Typography>
				</Box>
			</Box>
		</Box>
	)
}

// The heatmap: navy header with the pickup days, the LOR column fixed and white
const heatmapSx = {
	borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, minWidth: '100%',
	'& th': { backgroundColor: dsColorNew.tableHead, color: dsColorNew.white, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', p: 0 },
	'& th.mo': { textAlign: 'left', px: 0.75, pt: 0.9, pb: 0.25, borderLeft: `1px solid ${alpha(dsColorNew.white, 0.18)}` },
	'& th.dy': { fontWeight: 400, fontSize: 11, color: dataColors.onNavyMuted, minWidth: 42, textAlign: 'center', pb: 0.75 },
	'& td': { p: '3px 2px', borderBottom: `1px solid ${dataColors.tableLineFaint}`, minWidth: 42 },
	'& .fx': {
		position: 'sticky', left: 0, zIndex: 2, backgroundColor: dsColorNew.white, px: 1.5, minWidth: 56,
		fontWeight: 600, color: dsColorNew.textTitle, textAlign: 'left', boxShadow: `6px 0 8px -6px ${alpha(dsColorNew.textTitle, 0.18)}`,
	},
	'& thead th.fx': { backgroundColor: dsColorNew.tableHead, color: alpha(dsColorNew.white, 0.8), fontWeight: 500, zIndex: 3 },
}

// ---------------------------------------------------------------------------
// Cell zoom — provider-level prices for a single (office × sipp × pickup × LOR)
// ---------------------------------------------------------------------------
function quantile(sorted, q) {
	if (!sorted.length) return null
	const pos  = (sorted.length - 1) * q
	const base = Math.floor(pos)
	const rest = pos - base
	if (sorted[base + 1] !== undefined) {
		return sorted[base] + rest * (sorted[base + 1] - sorted[base])
	}
	return sorted[base]
}

// Smooth numeric tween — used to animate big numbers when the user
// toggles a site/provider chip. Eased with cubic ease-out, ~380ms.
function useAnimatedNumber(value, duration = 380) {
	const [display, setDisplay] = useState(value)
	const fromRef = useRef(value)
	useEffect(() => {
		if (value == null) {
			setDisplay(null); fromRef.current = null; return
		}
		const from = fromRef.current
		if (from == null || from === value) {
			setDisplay(value); fromRef.current = value; return
		}
		let raf
		const start = performance.now()
		const tick = (now) => {
			const t = Math.min(1, (now - start) / duration)
			const eased = 1 - Math.pow(1 - t, 3)
			setDisplay(from + (value - from) * eased)
			if (t < 1) raf = requestAnimationFrame(tick)
			else fromRef.current = value
		}
		raf = requestAnimationFrame(tick)
		return () => cancelAnimationFrame(raf)
	}, [value, duration])
	return display
}

function formatPickupLong(iso) {
	if (!iso) return ''
	const d = new Date(`${iso}T00:00:00`)
	const wd = d.toLocaleDateString('en-GB', { weekday: 'short' })
	const mo = d.toLocaleDateString('en-GB', { month: 'long' })
	return `${wd} ${d.getDate()} ${mo} ${d.getFullYear()}`
}

// A site / competitor chip of the dialog: off dashed and struck through
// A site or competitor left out keeps its struck-through name, on the app's filter chip
const chipToggleSx = (off) => off ? {
	borderStyle: 'dashed',
	'& .MuiTypography-root': { textDecoration: 'line-through', color: dsColorNew.textSubtle },
} : {}

function CellDetailDialog({ open, onClose, office, cat, pickup, lor, leaf, filters }) {
	const [data, setData] = useState(null)
	const [fleet, setFleet] = useState(null)
	const [error, setError] = useState(null)
	const [loading, setLoading] = useState(true)
	const [disabledSites,     setDisabledSites]     = useState(() => new Set())
	const [disabledProviders, setDisabledProviders] = useState(() => new Set())

	useEffect(() => {
		setLoading(true); setError(null); setData(null); setFleet(null)
		setDisabledSites(new Set())
		setDisabledProviders(new Set())
		const qs = new URLSearchParams({
			office: office.office_code,
			sipp:   cat.sipp_code,
			pickup,
			lor:    String(lor),
		})
		const providerIds = new Set(filters?.providers || [])
		;(filters?.tierProviders || []).forEach(p => providerIds.add(p))
		if (providerIds.size) qs.set('providers', [...providerIds].join(','))
		if (filters?.markets && filters.markets.length)
			qs.set('markets', filters.markets.join(','))
		const fleetQs = new URLSearchParams({
			office: office.office_code, sipp: cat.sipp_code,
			pickup, lor: String(lor),
		})
		Promise.all([
			fetch(`/api/my-business/market/positioning/cell?${qs}`, { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
			fetch(`/api/my-business/pms/fleet-snapshot?${fleetQs}`, { credentials: 'include' })
				.then(r => r.ok ? r.json() : null)
				.catch(() => null),
		])
			.then(([d, f]) => { setData(d); setFleet(f) })
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [office.office_code, cat.sipp_code, pickup, lor])

	// Union of sites contributing to this cell, sorted by quote count.
	const allSites = useMemo(() => {
		if (!data?.providers) return []
		const m = new Map()
		data.providers.forEach(p => p.sites.forEach(s => {
			const prev = m.get(s.site_id)
			if (prev) prev.n_quotes++
			else m.set(s.site_id, { site_id: s.site_id, site_name: s.site_name, n_quotes: 1 })
		}))
		return [...m.values()].sort((a, b) => b.n_quotes - a.n_quotes)
	}, [data])

	// Competitor list for the provider chip row (excludes own).
	const competitors = useMemo(() => {
		if (!data?.providers) return []
		return data.providers
			.filter(p => !p.is_own)
			.map(p => ({ provider_id: p.provider_id, provider_name: p.provider_name,
			             tier: p.tier, n_sites: p.sites.length }))
	}, [data])

	// Client-side recompute when sites or providers are toggled — no fetch.
	const view = useMemo(() => {
		if (!data?.providers) return null
		if (disabledSites.size === 0 && disabledProviders.size === 0) {
			return { summary: data.summary || {}, providers: data.providers }
		}
		const filtered = data.providers
			.filter(p => p.is_own || !disabledProviders.has(p.provider_id))
			.map(p => {
				const sites = p.sites.filter(s => !disabledSites.has(s.site_id))
				if (sites.length === 0) return null
				const prices = sites.map(s => s.price).sort((a, b) => a - b)
				return { ...p, sites,
				         min:    prices[0],
				         max:    prices[prices.length - 1],
				         median: quantile(prices, 0.5) }
			}).filter(Boolean).sort((a, b) => a.median - b.median)

		const ownPrices = filtered.filter(p => p.is_own)
		                          .flatMap(p => p.sites.map(s => s.price))
		                          .sort((a, b) => a - b)
		const mktPrices = filtered.filter(p => !p.is_own)
		                          .flatMap(p => p.sites.map(s => s.price))
		                          .sort((a, b) => a - b)
		const myMed  = ownPrices.length ? quantile(ownPrices, 0.5) : null
		const mktMed = mktPrices.length ? quantile(mktPrices, 0.5) : null
		const summary = {
			my_median:     myMed,
			market_median: mktMed,
			market_p25:    mktPrices.length ? quantile(mktPrices, 0.25) : null,
			market_p75:    mktPrices.length ? quantile(mktPrices, 0.75) : null,
			market_min:    mktPrices.length ? mktPrices[0] : null,
			market_max:    mktPrices.length ? mktPrices[mktPrices.length - 1] : null,
			n_providers:   filtered.filter(p => !p.is_own).length,
			n_quotes:      mktPrices.length,
			index:         (myMed != null && mktMed) ? (myMed / mktMed) * 100 : null,
		}
		return { summary, providers: filtered }
	}, [data, disabledSites, disabledProviders])

	const toggleSite = (siteId) => setDisabledSites(prev => {
		const next = new Set(prev)
		if (next.has(siteId)) next.delete(siteId); else next.add(siteId)
		return next
	})
	const toggleProvider = (providerId) => setDisabledProviders(prev => {
		const next = new Set(prev)
		if (next.has(providerId)) next.delete(providerId); else next.add(providerId)
		return next
	})

	const summary = view?.summary || {}
	const band    = indexBand(summary.index)
	const deltaEur = (summary.my_median != null && summary.market_median != null)
		? summary.my_median - summary.market_median : null
	const deltaPct = (summary.my_median != null && summary.market_median)
		? ((summary.my_median - summary.market_median) / summary.market_median) * 100 : null
	const hasSpread = summary.market_min != null && summary.market_max != null
		&& summary.market_min !== summary.market_max

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="lg">
			<AppDialogHeader icon="magnifying-glass-chart"
			                 onClose={onClose}
			                 title={<>
				                 {formatPickupLong(pickup)}
				                 <Box component="span" sx={{ color: dsColorNew.textMuted, fontWeight: 500, ml: 1 }}>
					                 · {lor}-day rental
				                 </Box>
			                 </>}
			                 subtitle={`${office.office_code} · ${office.office_name} · ${cat.sipp_code}`}/>
			<AppDialogBody sx={{ p: 0, borderTop: `1px solid ${dsColorNew.grayBorder}`, overflowY: 'auto', minHeight: 0 }}>
			<Box sx={mbScopeSx}>
				{loading && <Box sx={{ p: 3 }}><AppResultsSkeleton rows={5} columns={5}/></Box>}
				{error && <AppNote severity="error" sx={{ m: 2 }}>{error}</AppNote>}
				{data && !loading && (
					<>
						{data.blocked_by && (
							<AppNote severity="warning" sx={{ m: 2 }}>{data.blocked_by}</AppNote>
						)}
						{!data.blocked_by && view && view.providers.length === 0 && (
							<Box sx={{ p: 3 }}>
								<AppEmptyLine>
									No quotes in this cell{disabledSites.size > 0 ? ' for the selected sites.' : '.'}
								</AppEmptyLine>
							</Box>
						)}
						{!data.blocked_by && view && view.providers.length > 0 && (
							<Box>
								{/* Top summary — index + 3 metric tiles */}
								<Box sx={{ p: 3, bgcolor: dsColorNew.contentBg }}>
									<Box display="flex" gap={2.5} alignItems="stretch" flexWrap="wrap">
										<IndexTile index={summary.index} band={band}/>
										<MetricTile label="Your price"    value={summary.my_median}/>
										<MetricTile label="Market median" value={summary.market_median}/>
										<DeltaTile  deltaEur={deltaEur} deltaPct={deltaPct}/>
										<FleetTile  fleet={fleet} leaf={leaf}/>
									</Box>
									{hasSpread && (
										<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 1.5 }}>
											Market range: {fmtEur(summary.market_min)} – {fmtEur(summary.market_max)}
											{' · '}p25–p75 {fmtEur(summary.market_p25)} – {fmtEur(summary.market_p75)}
										</Typography>
									)}
								</Box>
								{/* Site toggle row */}
								{allSites.length > 0 && (
									<>
										<Divider/>
										<Box sx={{ px: 3, py: 2, display: 'flex',
										            alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
											<Typography sx={{ fontSize: 11, fontWeight: 600,
											                   letterSpacing: 1,
											                   textTransform: 'uppercase',
											                   color: dsColorNew.textMuted, mr: 1 }}>
												Sites included
											</Typography>
											{allSites.map(s => {
												const off = disabledSites.has(s.site_id)
												return (
													<AppFilterChip key={s.site_id}
													               label={s.site_name}
													               selected={!off}
													               onClick={() => toggleSite(s.site_id)}
													               sx={chipToggleSx(off)}/>
												)
											})}
											{disabledSites.size > 0 && (
												<Button size="small" variant="text" onClick={() => setDisabledSites(new Set())}
												        sx={{ ml: 0.5, textTransform: 'none' }}>
													Reset
												</Button>
											)}
										</Box>
									</>
								)}
								{/* Provider toggle row — competitors only */}
								{competitors.length > 0 && (
									<>
										<Divider/>
										<Box sx={{ px: 3, py: 2, display: 'flex',
										            alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
											<Typography sx={{ fontSize: 11, fontWeight: 600,
											                   letterSpacing: 1,
											                   textTransform: 'uppercase',
											                   color: dsColorNew.textMuted, mr: 1 }}>
												Competitors included
											</Typography>
											{competitors.map(p => {
												const off = disabledProviders.has(p.provider_id)
												return (
													<AppFilterChip key={p.provider_id}
													               label={p.provider_name}
													               selected={!off}
													               onClick={() => toggleProvider(p.provider_id)}
													               sx={chipToggleSx(off)}/>
												)
											})}
											{disabledProviders.size > 0 && (
												<Button size="small" variant="text"
												        onClick={() => setDisabledProviders(new Set())}
												        sx={{ ml: 0.5, textTransform: 'none' }}>
													Reset
												</Button>
											)}
										</Box>
									</>
								)}
								<Divider/>
								{/* Provider price ladder */}
								<Box sx={{ p: 2.5 }}>
									<AppCardTitle icon="ranking-star" title="Providers in this slot, cheapest first"
									              sx={{ mb: 1.5, flexWrap: 'wrap' }}>
										<Typography component="span" sx={{ fontSize: 13, color: dsColorNew.textMuted, ml: 0.5 }}>
											{summary.n_providers} competitor{summary.n_providers === 1 ? '' : 's'}
											{' · '}{summary.n_quotes} quote{summary.n_quotes === 1 ? '' : 's'}
										</Typography>
									</AppCardTitle>
									<ProviderLadder providers={view.providers}
									                summary={summary}/>
								</Box>
							</Box>
						)}
					</>
				)}
			</Box>
			</AppDialogBody>
		</AppDialog>
	)
}

function IndexTile({ index, band }) {
	const v = useAnimatedNumber(index)
	return (
		<Box sx={{
			bgcolor: band.bg, color: band.color,
			transition: 'background-color 300ms ease',
			px: 2.5, py: 1.5, borderRadius: '12px',
			minWidth: 130, textAlign: 'center',
			fontVariantNumeric: 'tabular-nums',
			display: 'flex', flexDirection: 'column',
			alignItems: 'center', justifyContent: 'center',
		}}>
			<Box sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
				{v != null ? (Math.round(v * 10) / 10).toFixed(1) : '—'}
			</Box>
			<Box sx={{ fontSize: 11, fontWeight: 600,
			            letterSpacing: '0.06em', textTransform: 'uppercase',
			            opacity: 0.85, mt: 0.5 }}>
				{band.label}
			</Box>
		</Box>
	)
}

function MetricTile({ label, value }) {
	const v = useAnimatedNumber(value)
	return (
		<Box sx={{
			flex: '1 1 140px', minWidth: 140,
			bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
			borderRadius: '12px', px: 2, py: 1.5,
		}}>
			<Typography sx={{ fontSize: 11, fontWeight: 600,
			                   letterSpacing: 1, textTransform: 'uppercase',
			                   color: dsColorNew.textMuted }}>
				{label}
			</Typography>
			<Typography sx={{ fontSize: 24, fontWeight: 700, color: dsColorNew.textTitle,
			                   fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
				{v != null ? fmtEur(v) : '—'}
			</Typography>
		</Box>
	)
}

function DeltaTile({ deltaEur, deltaPct }) {
	const eur = useAnimatedNumber(deltaEur)
	const pct = useAnimatedNumber(deltaPct)
	if (eur == null || pct == null) {
		return (
			<Box sx={{
				flex: '1 1 140px', minWidth: 140,
				bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
				borderRadius: '12px', px: 2, py: 1.5,
			}}>
				<Typography sx={{ fontSize: 11, fontWeight: 600,
				                   letterSpacing: 1, textTransform: 'uppercase',
				                   color: dsColorNew.textMuted }}>
					Delta vs market
				</Typography>
				<Typography sx={{ fontSize: 24, fontWeight: 700,
				                   color: dsColorNew.textSubtle, mt: 0.5 }}>
					—
				</Typography>
			</Box>
		)
	}
	const positive = eur > 0
	const color = positive ? dsColorNew.dangerRed : dsColorNew.successText
	const sign  = positive ? '+' : ''
	return (
		<Box sx={{
			flex: '1 1 140px', minWidth: 140,
			bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
			borderRadius: '12px', px: 2, py: 1.5,
		}}>
			<Typography sx={{ fontSize: 11, fontWeight: 600,
			                   letterSpacing: 1, textTransform: 'uppercase',
			                   color: dsColorNew.textMuted }}>
				Delta vs market
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
				<Typography sx={{ fontSize: 24, fontWeight: 700,
				                   fontVariantNumeric: 'tabular-nums',
				                   transition: 'color 250ms ease',
				                   color }}>
					{sign}{fmtEur(Math.abs(eur) < 0.005 ? 0 : eur)}
				</Typography>
				<Typography sx={{ fontSize: 14, fontWeight: 600,
				                   transition: 'color 250ms ease',
				                   color }}>
					{sign}{(Math.round(pct * 10) / 10).toFixed(1)}%
				</Typography>
			</Box>
		</Box>
	)
}

function FleetTile({ fleet, leaf }) {
	const minAvail = useAnimatedNumber(fleet?.available_min ?? null)
	const totalFleet = fleet?.total_fleet ?? null
	const util = useAnimatedNumber(fleet?.utilization_max ?? null)
	const hasData = fleet && fleet.days_covered > 0 && totalFleet != null

	const paceStatus = leaf?.pace_status
	const paceActual   = leaf?.pace_actual
	const paceExpected = leaf?.pace_expected
	const dba          = leaf?.dba

	if (!hasData) {
		return (
			<Box sx={{
				flex: '1 1 180px', minWidth: 180,
				bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
				borderRadius: '12px', px: 2, py: 1.5,
			}}>
				<Typography sx={{ fontSize: 11, fontWeight: 600,
				                   letterSpacing: 1, textTransform: 'uppercase',
				                   color: dsColorNew.textMuted }}>
					Fleet · pace
				</Typography>
				<Typography sx={{ fontSize: 24, fontWeight: 700,
				                   color: dsColorNew.textSubtle, mt: 0.5 }}>
					—
				</Typography>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.25 }}>
					No fleet data
				</Typography>
			</Box>
		)
	}

	const dayLabel = fleet.days_covered === 1 ? 'day' : 'days'
	return (
		<Tooltip arrow placement="top"
		         title={
			         <Box sx={{ p: 0.5, minWidth: 160 }}>
				         {fleet.days.map(d => (
					         <Box key={d.date} sx={{ display: 'flex', gap: 1.5,
					                                  alignItems: 'baseline',
					                                  fontSize: 12,
					                                  fontVariantNumeric: 'tabular-nums' }}>
						         <Box sx={{ flex: 1 }}>{displayDay(d.date)}</Box>
						         <Box sx={{ fontWeight: 700 }}>{d.available}/{d.total}</Box>
						         <Box sx={{ opacity: 0.85, minWidth: 42, textAlign: 'right' }}>
							         {d.util_pct != null ? `${Math.round(d.util_pct)}%` : '—'}
						         </Box>
					         </Box>
				         ))}
			         </Box>
		         }>
			<Box sx={{
				flex: '1 1 180px', minWidth: 180,
				bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
				borderRadius: '12px', px: 2, py: 1.5, cursor: 'help',
			}}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					<Typography sx={{ fontSize: 11, fontWeight: 600,
					                   letterSpacing: 1, textTransform: 'uppercase',
					                   color: dsColorNew.textMuted }}>
						Fleet · pace
					</Typography>
					{paceStatus && (
						<Box sx={{
							width: 14, height: 14, borderRadius: '50%',
							bgcolor: PACE_COLOR[paceStatus], color: dsColorNew.white,
							lineHeight: 1,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
						}}>
							<PaceGlyph status={paceStatus} size={9}/>
						</Box>
					)}
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5 }}>
					<Typography sx={{ fontSize: 24, fontWeight: 700, color: dsColorNew.textTitle,
					                   fontVariantNumeric: 'tabular-nums' }}>
						{minAvail != null ? Math.round(minAvail) : '—'}
					</Typography>
					<Typography sx={{ fontSize: 14, color: dsColorNew.textMuted,
					                   fontVariantNumeric: 'tabular-nums' }}>
						/ {totalFleet}
					</Typography>
					<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted,
					                   ml: 0.5 }}>
						avail
					</Typography>
				</Box>
				<Typography sx={{ fontSize: 12, mt: 0.25,
				                   color: dsColorNew.textMuted }}>
					{util != null ? `${Math.round(util)}% util` : '—'}
					{paceExpected != null && (
						<> · expects ~{Math.round(paceExpected)}%</>
					)}
					{dba != null && <> · {dba}d to pickup</>}
				</Typography>
				{paceStatus && (
					<Typography sx={{ fontSize: 12.5, mt: 0.5,
					                   fontWeight: 600,
					                   color: PACE_COLOR[paceStatus] }}>
						{PACE_LABEL[paceStatus]}
					</Typography>
				)}
				<Typography sx={{ fontSize: 11.5, color: dsColorNew.textSubtle, mt: 0.25 }}>
					peak day of {fleet.days_covered} {dayLabel}
				</Typography>
			</Box>
		</Tooltip>
	)
}

function ProviderLadder({ providers, summary }) {
	const maxPrice = providers.reduce((m, p) => Math.max(m, p.max), 0) || 1
	const tierColor = {
		'You':      dsColorNew.webBlue500,
		'Low-cost': dsColorNew.successBar,
		'Mid-tier': dsColorNew.textSubtle,
		'Premium':  dataColors.violet500,
	}
	return (
		<Box>
			{providers.map((p) => {
				const isOwn   = p.is_own
				const nSites  = p.sites.length
				const showRange = p.min != null && p.max != null && p.min !== p.max
				return (
					<Box key={p.provider_id} sx={{
						display: 'grid',
						gridTemplateColumns: '220px 1fr 110px 130px',
						gap: 2, alignItems: 'center',
						py: 1.5, px: 1.5,
						borderBottom: `1px solid ${dsColorNew.grayBorder}`,
						borderLeft: '4px solid',
						borderLeftColor: isOwn ? dsColorNew.webBlue500 : 'transparent',
						bgcolor: isOwn ? dsColorNew.pillActiveBg : 'transparent',
						borderRadius: '4px 0 0 4px',
						transition: 'background-color 300ms ease',
					}}>
						<Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
								<Box sx={{
									fontWeight: 600, fontSize: 14,
									color: isOwn ? dsColorNew.webBlue500 : dsColorNew.textTitle,
								}}>
									{p.provider_name}
								</Box>
								<Box component="span"
								     sx={{
									     bgcolor: alpha(tierColor[p.tier] || dsColorNew.textSubtle, 0.14),
									     color: darken(tierColor[p.tier] || dsColorNew.textSubtle, 0.35), fontWeight: 600,
									     fontSize: 11, lineHeight: '20px',
									     px: 1, borderRadius: '10px',
									     whiteSpace: 'nowrap',
								     }}>
									{p.tier}
								</Box>
							</Box>
							<Box sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.35 }}>
								<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textDark }}>
									{nSites}
								</Box> site{nSites !== 1 ? 's' : ''}
								{nSites > 0 && (
									<> · {p.sites.slice(0, 3).map(s => s.site_name).join(', ')}
										{nSites > 3 ? ` +${nSites - 3}` : ''}</>
								)}
							</Box>
						</Box>
						<Tooltip arrow placement="top"
						         title={p.sites.map(s => `${s.site_name}: ${fmtEur(s.price)}`).join('\n')}
						         componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' }}}}>
							<Box sx={{ position: 'relative', height: 10,
							           bgcolor: dsColorNew.pillTrackBg, borderRadius: '5px',
							           cursor: 'help' }}>
								<Box sx={{
									position: 'absolute', left: 0, top: 0, bottom: 0,
									width: `${(p.median / maxPrice) * 100}%`,
									bgcolor: tierColor[p.tier] || dsColorNew.textSubtle,
									borderRadius: '5px', opacity: isOwn ? 1 : 0.6,
									transition: 'width 380ms cubic-bezier(0.22,1,0.36,1)',
								}}/>
								{summary.market_median != null && (
									<Box sx={{
										position: 'absolute',
										left: `${(summary.market_median / maxPrice) * 100}%`,
										top: -4, bottom: -4, width: 2,
										bgcolor: dsColorNew.navy,
										transition: 'left 380ms cubic-bezier(0.22,1,0.36,1)',
									}}/>
								)}
							</Box>
						</Tooltip>
						<Box sx={{
							textAlign: 'right', fontWeight: 700,
							fontSize: 16, fontVariantNumeric: 'tabular-nums',
							color: isOwn ? dsColorNew.webBlue500 : dsColorNew.textTitle,
						}}>
							{fmtEur(p.median)}
						</Box>
						<Box sx={{ textAlign: 'right',
						           fontSize: 12, color: dsColorNew.textMuted,
						           fontVariantNumeric: 'tabular-nums' }}>
							{showRange
								? `${fmtEur(p.min)} – ${fmtEur(p.max)}`
								: nSites === 1 ? 'single quote' : ''}
						</Box>
					</Box>
				)
			})}
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1.5 }}>
				Bar = provider's median price. Vertical black line marks the market median.
				Hover a bar to see prices per site.
			</Typography>
		</Box>
	)
}

function BlockedAlert({ reason }) {
	const cases = {
		no_provider_id_for_tenant: 'No provider ID configured for this tenant. The price-positioning view needs to know which Price Seeker provider ID represents your brand. Ask the team to map it.',
		no_office_mappings:        'No office mappings configured yet. Go to Mapping Admin → Offices first.',
		no_category_mappings:      'No category mappings configured yet. Go to Mapping Admin → Categories first.',
	}
	return <AppNote severity="warning">{cases[reason] || reason}</AppNote>
}
